import { useState, useEffect, useCallback } from 'react'
import {
  getMailCampaigns,
  getMailCampaign,
  deleteMailCampaign,
  type MailCampaignRead,
  type MailCampaignStatus,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { CampaignFormModal } from './CampaignFormModal'
import { CampaignDetailModal } from './CampaignDetailModal'
import { ServiceNoticeMark } from './CampaignKindPicker'

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  sending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  sent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

const PAGE_SIZE = 20

export function Campaigns() {
  const [campaigns, setCampaigns] = useState<MailCampaignRead[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [statusFilter, setStatusFilter] = useState<MailCampaignStatus | ''>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<MailCampaignRead | null>(null)
  const [editingCampaign, setEditingCampaign] = useState<MailCampaignRead | null>(null)

  // search is client-side filter on name since the new API doesn't support text search
  const [search, setSearch] = useState('')

  const canCreate = usePermission('email_campaigns', 'create')

  const loadCampaigns = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getMailCampaigns({
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      if (response.ok && response.data) {
        setCampaigns(response.data.items)
        setTotal(response.data.total)
      } else {
        setError(response.error?.message || 'Failed to load campaigns')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, offset])

  useEffect(() => {
    loadCampaigns()
  }, [loadCampaigns])

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0)
  }, [statusFilter])

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this campaign? This cannot be undone.')) return
    const response = await deleteMailCampaign(id)
    if (response.ok) loadCampaigns()
  }

  const handleViewCampaign = async (id: number) => {
    const response = await getMailCampaign(id)
    if (response.ok && response.data) {
      setSelectedCampaign(response.data)
    }
  }

  const handleEditCampaign = () => {
    if (selectedCampaign) {
      setEditingCampaign(selectedCampaign)
      setSelectedCampaign(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1

  const filteredCampaigns = search.trim()
    ? campaigns.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.subject.toLowerCase().includes(search.toLowerCase())
      )
    : campaigns

  const selectStyles = `rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2 text-sm transition-all duration-200 focus:border-[#503864] dark:focus:border-[#7f4f9a] focus:outline-none focus:ring-2 focus:ring-[#503864]/20 dark:focus:ring-[#7f4f9a]/20`

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100">Email Campaigns</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} total campaigns</p>
        </div>
        {canCreate && (
          <CustomButton onClick={() => setShowCreateModal(true)} variant="primary">
            New Campaign
          </CustomButton>
        )}
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as MailCampaignStatus | '')}
          className={selectStyles}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sending">Sending</option>
          <option value="sent">Sent</option>
          <option value="cancelled">Cancelled</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">No campaigns found</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border-card dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-card dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Subject</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Audience</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Recipients</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-card dark:divide-gray-700">
              {filteredCampaigns.map((campaign) => (
                <tr
                  key={campaign.id}
                  className="bg-white dark:bg-[#1a1520] hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  onClick={() => handleViewCampaign(campaign.id)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    {campaign.name}
                    {campaign.kind === 'service_notice' && <ServiceNoticeMark />}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-[200px] truncate">
                    {campaign.subject}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[campaign.status] ?? ''}`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-[200px] truncate">
                    {campaign.audience_summary}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">
                    {campaign.recipient_count.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <a
                        href={campaign.listmonk_admin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#503864] dark:text-[#9f7aba] hover:underline"
                        title="Open in Listmonk"
                      >
                        Listmonk
                      </a>
                      {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                        <CustomButton
                          onClick={() => handleDelete(campaign.id)}
                          variant="secondary"
                        >
                          Delete
                        </CustomButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            disabled={offset === 0}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <CustomButton
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {showCreateModal && (
        <CampaignFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={loadCampaigns}
        />
      )}

      {selectedCampaign && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onUpdate={loadCampaigns}
          onEdit={handleEditCampaign}
        />
      )}

      {editingCampaign && (
        <CampaignFormModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSave={loadCampaigns}
        />
      )}
    </div>
  )
}
